import React, { useState } from "react";
import FullScreenPanel from "./FullScreenPanel.jsx";
import "../mobile.css";

/**
 * A phone list: search box, a Filters button that opens a full-screen filter panel, then one
 * card per row (plan section 4: replaces the 13-17 column tables). Layout only; the caller
 * owns the rows, the search text and the filter state.
 *
 *   <CardList
 *     items={rows} getKey={(r) => r.id} renderCard={(r) => <...>} onItemClick={(r) => ...}
 *     search={{ value, onChange, placeholder }}
 *     filters={{ activeCount, render: () => <controls/>, onClear, resultLabel }}
 *     loading error empty="No orders match." />
 */
export default function CardList({
  items = [],
  getKey = (item, i) => i,
  renderCard,
  onItemClick = null,
  search = null,
  filters = null,
  loading = false,
  error = null,
  empty = "Nothing to show.",
  testId = "card-list",
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeCount = (filters && filters.activeCount) || 0;

  return (
    <div className="jq-card-list" data-testid={testId}>
      {(search || filters) && (
        <div className="jq-list-tools">
          {search && (
            <div className="jq-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                className="form-control"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && search.onSubmit) search.onSubmit(search.value); }}
                placeholder={search.placeholder || "Search"}
                aria-label={search.placeholder || "Search"}
              />
            </div>
          )}
          {filters && (
            <button type="button" className={`btn ${activeCount ? "btn-primary" : "btn-outline-secondary"} jq-filter-btn`} onClick={() => setFiltersOpen(true)}>
              <i className="fas fa-sliders-h" aria-hidden="true" /> Filters{activeCount ? ` (${activeCount})` : ""}
            </button>
          )}
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {loading && (
        <div className="jq-list-state" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" /> Loading…
        </div>
      )}
      {!loading && !error && items.length === 0 && <div className="jq-list-state">{empty}</div>}

      {!loading && items.map((item, i) => {
        const body = renderCard(item, i);
        return onItemClick ? (
          <button type="button" key={getKey(item, i)} className="jq-card jq-card-button" onClick={() => onItemClick(item, i)}>
            {body}
            <i className="fas fa-chevron-right jq-card-chevron" aria-hidden="true" />
          </button>
        ) : (
          <div key={getKey(item, i)} className="jq-card">{body}</div>
        );
      })}

      {filters && (
        <FullScreenPanel
          open={filtersOpen}
          title="Filters"
          onClose={() => setFiltersOpen(false)}
          testId="card-list-filters"
          footer={
            <div className="jq-two-buttons">
              <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => filters.onClear && filters.onClear()} disabled={!activeCount}>
                Clear
              </button>
              <button type="button" className="btn btn-primary btn-lg" onClick={() => setFiltersOpen(false)}>
                {filters.resultLabel || "Show results"}
              </button>
            </div>
          }
        >
          {filters.render()}
        </FullScreenPanel>
      )}
    </div>
  );
}
